"use client";

import type { AssistantStaticBubbleVariantProps, UserStaticBubbleVariantProps } from "@ui/components/core/BaseChat";
import type { Transition } from "motion/react";
import * as m from "motion/react-m";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { getMessageAlignmentClassName } from "./alignment";
import { MessageAvatar } from "./MessageAvatar";
import { MessagePartsList } from "./messageParts";
import type { JSX } from "react";
import type { ChatScenarioAvatar, ChatScenarioMessage } from "./types";

const messageEnterTransition: Transition = { type: "spring", stiffness: 300, damping: 25 };

interface AnimatedScenarioMessageProps {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  message: ChatScenarioMessage;
  messageIndex: number;
  playbackRunId: number;
  onMessageComplete: (messageIndex: number, playbackRunId: number) => void;
  partDelayMS?: number;
  userMessageVariants: UserStaticBubbleVariantProps;
}

interface StaticScenarioMessageProps {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  message: ChatScenarioMessage;
  userMessageVariants: UserStaticBubbleVariantProps;
}

interface MessageWithAvatarProps {
  avatar?: ChatScenarioAvatar;
  role: ChatScenarioMessage["role"];
  children: React.ReactNode;
}

/** Wraps message content with an optional avatar, positioned based on role */
function MessageWithAvatar({ avatar, role, children }: MessageWithAvatarProps): JSX.Element {
  if (!avatar) {
    return <>{children}</>;
  }

  const avatarElement = <MessageAvatar avatar={avatar} />;

  if (role === "user") {
    return (
      <div className='tw-flex tw-items-start tw-gap-2'>
        <div className='tw-min-w-0 tw-flex-1'>{children}</div>
        {avatarElement}
      </div>
    );
  }

  return (
    <div className='tw-flex tw-items-start tw-gap-2'>
      {avatarElement}
      <div className='tw-min-w-0 tw-flex-1'>{children}</div>
    </div>
  );
}

function UserMessage({
  assistantMessageVariants,
  message,
  messageIndex,
  playbackRunId,
  onMessageComplete,
  userMessageVariants,
}: AnimatedScenarioMessageProps): JSX.Element {
  const handleComplete = useCallback(() => {
    onMessageComplete(messageIndex, playbackRunId);
  }, [messageIndex, onMessageComplete, playbackRunId]);

  return (
    <m.div
      initial={{ opacity: 0, transform: "translateY(0.625rem)", scale: 0.95 }}
      animate={{ opacity: 1, transform: "translateY(0rem)", scale: 1 }}
      transition={messageEnterTransition}
      onAnimationComplete={handleComplete}
    >
      <MessageWithAvatar avatar={message.avatar} role={message.role}>
        <MessagePartsList
          messageId={message.id}
          role={message.role}
          parts={message.parts}
          assistantMessageVariants={assistantMessageVariants}
          userMessageVariants={userMessageVariants}
        />
      </MessageWithAvatar>
    </m.div>
  );
}

function AssistantMessage({
  assistantMessageVariants,
  message,
  messageIndex,
  playbackRunId,
  onMessageComplete,
  partDelayMS = 0,
  userMessageVariants,
}: AnimatedScenarioMessageProps): JSX.Element {
  const [visiblePartCount, setVisiblePartCount] = useState(0);
  const hasStartedRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const visiblePartCountRef = useRef(0);
  const partDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (partDelayTimerRef.current !== null) {
        clearTimeout(partDelayTimerRef.current);
        partDelayTimerRef.current = null;
      }
    };
  }, []);

  const handleComplete = useCallback(() => {
    onMessageComplete(messageIndex, playbackRunId);
  }, [messageIndex, onMessageComplete, playbackRunId]);

  const revealParts = useCallback((count: number) => {
    visiblePartCountRef.current = count;
    setVisiblePartCount(count);
  }, []);

  const handleMessageEnterComplete = useCallback(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (message.parts.length === 0) {
      hasCompletedRef.current = true;
      handleComplete();
      return;
    }

    revealParts(1);
  }, [handleComplete, message.parts.length, revealParts]);

  const handlePartComplete = useCallback(
    (partIndex: number) => {
      if (hasCompletedRef.current || visiblePartCountRef.current !== partIndex + 1) {
        return;
      }

      const nextVisiblePartCount = partIndex + 2;

      if (nextVisiblePartCount > message.parts.length) {
        hasCompletedRef.current = true;
        handleComplete();
        return;
      }

      partDelayTimerRef.current = setTimeout(() => {
        partDelayTimerRef.current = null;
        revealParts(nextVisiblePartCount);
      }, partDelayMS);
    },
    [handleComplete, message.parts.length, partDelayMS, revealParts],
  );

  return (
    <m.div
      initial={{ opacity: 0, transform: "translateY(0.625rem)", scale: 0.95 }}
      animate={{ opacity: 1, transform: "translateY(0rem)", scale: 1 }}
      transition={messageEnterTransition}
      onAnimationComplete={handleMessageEnterComplete}
    >
      <MessageWithAvatar avatar={message.avatar} role={message.role}>
        <MessagePartsList
          messageId={message.id}
          role={message.role}
          parts={message.parts}
          visiblePartCount={visiblePartCount}
          animateParts
          onPartComplete={handlePartComplete}
          assistantMessageVariants={assistantMessageVariants}
          userMessageVariants={userMessageVariants}
        />
      </MessageWithAvatar>
    </m.div>
  );
}

export const UserScenarioMessage = memo(UserMessage);
export const AssistantScenarioMessage = memo(AssistantMessage);

export function StaticMessage({
  message,
  assistantMessageVariants,
  userMessageVariants,
}: StaticScenarioMessageProps): JSX.Element {
  return (
    <div className={getMessageAlignmentClassName(message.role)}>
      <MessageWithAvatar avatar={message.avatar} role={message.role}>
        <MessagePartsList
          messageId={message.id}
          role={message.role}
          parts={message.parts}
          assistantMessageVariants={assistantMessageVariants}
          userMessageVariants={userMessageVariants}
        />
      </MessageWithAvatar>
    </div>
  );
}
